/**
 * African Fashion Theme — core JS
 * Announcement bar rotation, header scroll, mobile drawer, sticky ATC, variant picker.
 */

/* ============================================================
   OVERLAY ORCHESTRATOR
   Ensures only one overlay/drawer is visible at a time.

   Usage:
     window.Overlays.register('my-id', myCloseFn);   // called in each overlay block
     window.Overlays.closeAll('my-id');               // call at the start of each open fn
     window.Overlays.openAfter(openFn, delay);        // close all THEN run openFn after delay
   ============================================================ */
window.Overlays = (function () {
  var _registry = {};

  function register(id, closeFn) {
    _registry[id] = closeFn;
  }

  function closeAll(exceptId) {
    Object.keys(_registry).forEach(function (id) {
      if (id === exceptId) return;
      try { _registry[id](); } catch (_) {}
    });
  }

  /* Close all others first, then open after the CSS transition finishes */
  function openAfter(openFn, exceptId, delay) {
    closeAll(exceptId);
    setTimeout(openFn, delay || 360);
  }

  return { register: register, closeAll: closeAll, openAfter: openAfter };
})();

/* ============================================================
   FOCUS TRAP UTILITY
   Keeps keyboard Tab / Shift-Tab inside a modal or drawer.

   Usage:
     const trap = createFocusTrap(containerEl, triggerEl);
     trap.activate();    // call when overlay opens — focuses first element
     trap.deactivate();  // call when overlay closes — returns focus to trigger
   ============================================================ */
window.createFocusTrap = function (container, returnEl) {
  var FOCUSABLE = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function getFocusable() {
    // Filter out elements inside hidden containers
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return !el.closest('[hidden]') && !el.closest('[aria-hidden="true"]') && el.offsetParent !== null;
    });
  }

  function handleKey(e) {
    if (e.key !== 'Tab') return;
    var els = getFocusable();
    if (!els.length) { e.preventDefault(); return; }
    var first = els[0];
    var last  = els[els.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first || !container.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last || !container.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  return {
    activate: function () {
      document.addEventListener('keydown', handleKey);
      var els = getFocusable();
      if (els.length) els[0].focus();
    },
    deactivate: function () {
      document.removeEventListener('keydown', handleKey);
      if (returnEl && typeof returnEl.focus === 'function') {
        returnEl.focus();
      }
    }
  };
};

(function () {
  /* ============================================================
     ANNOUNCEMENT BAR — auto-rotate slides with pause control
     ============================================================ */
  const announcement = document.querySelector('[data-announcement]');
  if (announcement) {
    const slides = announcement.querySelectorAll('.announcement__slide');
    const pauseBtn = announcement.querySelector('[data-announcement-pause]');
    let current = 0;
    let interval = null;
    let paused = false;

    function rotateSlides() {
      slides[current].classList.remove('is-active');
      current = (current + 1) % slides.length;
      slides[current].classList.add('is-active');
    }

    function startRotation() {
      if (slides.length > 1) {
        interval = setInterval(rotateSlides, 4000);
      }
    }

    function stopRotation() {
      clearInterval(interval);
    }

    startRotation();

    if (pauseBtn) {
      pauseBtn.addEventListener('click', () => {
        paused = !paused;
        pauseBtn.classList.toggle('is-paused', paused);
        pauseBtn.setAttribute('aria-label', paused ? 'Resume slide rotation' : 'Pause slide rotation');
        if (paused) {
          stopRotation();
        } else {
          startRotation();
        }
      });
    }
  }

  /* ============================================================
     HEADER — sticky (fixed), hide-on-scroll, transparent-on-scroll
     ============================================================ */
  const header = document.querySelector('[data-header]');
  if (header) {
    const hideOnScroll = header.hasAttribute('data-hide-on-scroll');
    /* Slide the whole section wrapper so both the announcement bar and the
       header disappear together when hide-on-scroll is enabled. */
    const hideTarget = header.closest('.shopify-section') || header;
    let lastScrollY = 0;
    let headerHidden = false;
    let ticking = false;

    /* Exposed so the drawer can pause / resume scroll-driven effects */
    window._headerScrollPaused = false;

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;

        if (!window._headerScrollPaused && hideOnScroll) {
          if (y > 120 && y > lastScrollY + 5 && !headerHidden) {
            hideTarget.classList.add('header--hidden');
            headerHidden = true;
          } else if (y < lastScrollY - 5 && headerHidden) {
            hideTarget.classList.remove('header--hidden');
            headerHidden = false;
          }
        }

        lastScrollY = y;
        ticking = false;
      });
    }, { passive: true });
  }

  /* ============================================================
     MOBILE DRAWER — slide from left + drill-down page navigation
     ============================================================ */
  const drawer = document.querySelector('[data-drawer]');
  if (drawer) {
    /* Move drawer to <body> so it is never a descendant of the sticky header
       section. If it stayed inside, any transform applied to the section wrapper
       (e.g. header--hidden: translateY(-100%)) would break position:fixed on the
       drawer, pulling it off-screen with the header. */
    document.body.appendChild(drawer);

    const panel = drawer.querySelector('.drawer__panel');
    let _drawerOpen = false;   // guard: prevents close side-effects when already closed
    let _drawerTrigger = null; // the button that opened the drawer (for focus return)
    let _drawerTrap = null;    // active focus trap instance

    // ── Page navigation ──
    const rootPage   = drawer.querySelector('#drawer-page-root');
    let   activePage = rootPage;

    function goToPage(targetId) {
      const target = drawer.querySelector('#' + targetId);
      if (!target || target === activePage) return;
      activePage.classList.add('is-exit');   // slide current left
      target.classList.add('is-active');     // slide target in from right
      activePage = target;
    }

    function goBack() {
      if (!activePage || activePage === rootPage) return;
      const parentId = activePage.dataset.parentPage;
      const parent   = parentId ? drawer.querySelector('#' + parentId) : null;
      if (!parent) return;
      activePage.classList.remove('is-active'); // slide current out right
      parent.classList.remove('is-exit');       // slide parent back to centre
      activePage = parent;
    }

    function resetPages() {
      drawer.querySelectorAll('[data-drawer-page]').forEach(p => {
        p.classList.remove('is-active', 'is-exit');
      });
      if (rootPage) rootPage.classList.add('is-active');
      activePage = rootPage;
    }

    drawer.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-open-page]');
      if (openBtn) { goToPage(openBtn.dataset.openPage); return; }
      const backBtn = e.target.closest('[data-back-page]');
      if (backBtn) { goBack(); return; }
    });

    // ── Drawer open / close ──
    function openDrawer() {
      window.Overlays.closeAll('drawer');   // close search, cart, market, etc. first
      _drawerOpen = true;
      _drawerTrigger = document.activeElement;  // remember what had focus

      // Pause sticky/transparent/hide-on-scroll effects so the header doesn't
      // change state or slide away while the nav is open.
      window._headerScrollPaused = true;

      // iOS-safe scroll lock: overflow:hidden alone doesn't prevent scroll on iOS.
      // Save scroll position, fix the body in place, restore on close.
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.dataset.scrollY = scrollY;

      drawer.hidden = false;
      document.querySelector('[data-drawer-open]')?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        // Activate focus trap — focuses first element inside panel
        _drawerTrap = window.createFocusTrap(panel, _drawerTrigger);
        _drawerTrap.activate();
      }));
    }

    function closeDrawer() {
      if (!_drawerOpen) return;   // no-op if already closed — prevents side-effects
      _drawerOpen = false;
      if (_drawerTrap) { _drawerTrap.deactivate(); _drawerTrap = null; }
      drawer.classList.remove('is-open');

      // Resume header scroll effects and ensure header is visible again.
      window._headerScrollPaused = false;
      const hideTarget = document.querySelector('[data-header]')?.closest('.shopify-section')
                      || document.querySelector('[data-header]');
      if (hideTarget) hideTarget.classList.remove('header--hidden');

      // Restore scroll position after iOS-safe lock
      const savedScrollY = parseInt(document.body.dataset.scrollY || '0', 10);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      delete document.body.dataset.scrollY;
      window.scrollTo(0, savedScrollY);

      setTimeout(() => {
        drawer.hidden = true;
        resetPages(); // reset all pages back to root
        document.querySelector('[data-drawer-open]')?.setAttribute('aria-expanded', 'false');
      }, 350);
    }

    // Register so other overlays can close this drawer
    window.Overlays.register('drawer', closeDrawer);

    // ── Market button inside the drawer ──
    // Close the drawer first, then re-trigger the market modal after the animation.
    drawer.addEventListener('click', (e) => {
      const marketBtn = e.target.closest('[data-market-open]');
      if (!marketBtn) return;
      e.preventDefault();
      e.stopPropagation();   // don't let it bubble yet — we'll re-dispatch after close
      closeDrawer();
      setTimeout(() => {
        // Find the header-level market button and click it so the market modal's
        // own JS opens it (avoids duplicating market modal logic here).
        const headerMarket = document.querySelector('.header__icon--market[data-market-open]');
        if (headerMarket) {
          headerMarket.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        } else {
          // Fallback: re-dispatch on the original button (e.g. if header market is hidden)
          marketBtn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        }
      }, 360);  // just after the 350ms close animation
    });

    document.querySelectorAll('[data-drawer-open]').forEach(btn => btn.addEventListener('click', openDrawer));
    drawer.querySelectorAll('[data-drawer-close]').forEach(btn => btn.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.hidden) closeDrawer();
    });
  }

  /* ============================================================
     DESKTOP MEGA MENU — hover (with close delay) + click + keyboard
     ============================================================ */
  (function () {
    const navItems = document.querySelectorAll('[data-nav-item]');
    if (!navItems.length) return;
    const closeTimers = new Map();

    function openMenu(item) {
      clearTimeout(closeTimers.get(item));
      closeTimers.delete(item);
      navItems.forEach(other => {
        if (other !== item) forceClose(other);
      });
      item.classList.add('is-open');
      const link = item.querySelector('.header__nav-link');
      if (link) link.setAttribute('aria-expanded', 'true');
    }

    function scheduleClose(item) {
      clearTimeout(closeTimers.get(item));
      closeTimers.set(item, setTimeout(() => forceClose(item), 180));
    }

    function forceClose(item) {
      clearTimeout(closeTimers.get(item));
      closeTimers.delete(item);
      item.classList.remove('is-open');
      const link = item.querySelector('.header__nav-link');
      if (link) link.setAttribute('aria-expanded', 'false');
    }

    navItems.forEach(item => {
      if (!item.querySelector('[data-mega-menu]')) return;
      item.addEventListener('mouseenter', () => openMenu(item));
      item.addEventListener('mouseleave', () => scheduleClose(item));

      const navLink = item.querySelector('.header__nav-link');
      if (navLink) {
        navLink.addEventListener('click', e => {
          if (window.matchMedia('(min-width: 1024px)').matches) {
            if (!item.classList.contains('is-open')) {
              e.preventDefault();
              openMenu(item);
            }
            // already open → let the href navigate
          }
        });
      }
    });

    document.addEventListener('click', e => {
      if (!e.target.closest('[data-nav-item]')) {
        navItems.forEach(forceClose);
      }
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') navItems.forEach(forceClose);
    });
  })();

  /* ============================================================
     SEARCH OVERLAY — full-screen takeover (Gymshark-style)
     ============================================================ */
  const searchOverlay = document.querySelector('[data-search-overlay]');
  if (searchOverlay) {
    const searchInput = searchOverlay.querySelector('[data-search-input]');
    let _searchOpen = false;
    let _searchTrigger = null;
    let _searchTrap = null;

    function openSearch() {
      window.Overlays.closeAll('search');   // close drawer, cart, etc. first
      _searchOpen = true;
      _searchTrigger = document.activeElement;
      searchOverlay.hidden = false;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        _searchTrap = window.createFocusTrap(searchOverlay, _searchTrigger);
        // Focus the input directly (createFocusTrap focuses first focusable, which is input)
        if (searchInput) searchInput.focus();
      });
    }

    function closeSearch() {
      if (!_searchOpen) return;
      _searchOpen = false;
      if (_searchTrap) { _searchTrap.deactivate(); _searchTrap = null; }
      else if (_searchTrigger) { _searchTrigger.focus(); }
      searchOverlay.hidden = true;
      document.body.style.overflow = '';
    }

    // Register so other overlays can close search
    window.Overlays.register('search', closeSearch);

    // Open triggers — inline search bar (desktop) + search icon (mobile)
    document.querySelectorAll('[data-search-trigger], [data-search-open]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        openSearch();
      });
    });

    // Close
    document.querySelectorAll('[data-search-close]').forEach((btn) => {
      btn.addEventListener('click', closeSearch);
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !searchOverlay.hidden) closeSearch();
    });
  }

  /* ============================================================
     FILTER TOP BAR — pill dropdowns (one open at a time)
     ============================================================ */
  const cfTop = document.querySelector('[data-cf-top]');
  if (cfTop) {
    const groups = cfTop.querySelectorAll('[data-cf-dropdown]');

    function closeAllDropdowns(except) {
      groups.forEach((g) => {
        if (g === except) return;
        const panel = g.querySelector('[data-cf-dropdown-panel]');
        const btn = g.querySelector('[data-cf-dropdown-toggle]');
        if (panel) panel.hidden = true;
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }

    groups.forEach((group) => {
      const btn = group.querySelector('[data-cf-dropdown-toggle]');
      const panel = group.querySelector('[data-cf-dropdown-panel]');
      if (!btn || !panel) return;

      btn.setAttribute('aria-expanded', 'false');

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = !panel.hidden;
        closeAllDropdowns(group);
        panel.hidden = isOpen;
        btn.setAttribute('aria-expanded', String(!isOpen));
      });
    });

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (!cfTop.contains(e.target)) {
        closeAllDropdowns(null);
      }
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllDropdowns(null);
    });

    // Auto-submit on filter change (not price inputs — those need manual submit)
    cfTop.addEventListener('change', (e) => {
      const target = e.target;
      if (target.matches('[data-cf-sort]') || (target.matches('[data-cf-input]') && !target.matches('.cf__price-input'))) {
        target.closest('form').submit();
      }
    });
  }

  /* ============================================================
     FILTER DRAWER — fully lazy: querySelector at click-time so
     it works regardless of when the element enters the DOM or
     whether earlier JS threw an error before this block.
     ============================================================ */
  let _filterDrawerOpen = false;   // guard for filter drawer

  function openFilterDrawer() {
    const fd = document.querySelector('[data-filter-drawer]');
    if (!fd) return;
    window.Overlays.closeAll('filter-drawer');   // close nav drawer, search, etc. first
    _filterDrawerOpen = true;
    fd.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => requestAnimationFrame(() => fd.classList.add('is-open')));
  }
  function closeFilterDrawer() {
    if (!_filterDrawerOpen) return;   // no-op if already closed
    _filterDrawerOpen = false;
    const fd = document.querySelector('[data-filter-drawer]');
    if (!fd) return;
    fd.classList.remove('is-open');
    setTimeout(() => {
      fd.hidden = true;
      document.body.style.overflow = '';
    }, 350);
  }

  // Register filter drawer so other overlays can close it
  window.Overlays.register('filter-drawer', closeFilterDrawer);

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-filter-drawer-open]'))  { openFilterDrawer();  return; }
    if (e.target.closest('[data-filter-drawer-close]')) { closeFilterDrawer(); return; }
  });
  document.addEventListener('keydown', (e) => {
    const fd = document.querySelector('[data-filter-drawer]');
    if (e.key === 'Escape' && fd && !fd.hidden) closeFilterDrawer();
  });

  /* Filter checkboxes — auto-submit sidebar + top-bar on change */
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!input.matches('input[type="checkbox"]')) return;
    const form = input.closest('[data-cf-form]');
    if (!form) return;
    // Drawer uses explicit Apply button — skip
    if (form.closest('.filter-drawer__body')) return;
    // Close any open top-bar dropdown before navigating
    const openPanel = input.closest('[data-cf-dropdown-panel]');
    if (openPanel) openPanel.hidden = true;
    // Submit (AJAX interceptor handles it)
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  });

  /* ============================================================
     TOP-BAR FILTER DROPDOWNS — fully event-delegated
     Works on first load AND after every AJAX DOM swap without
     needing any reinit call.
     ============================================================ */
  function closeAllDropdowns(exceptGroup) {
    document.querySelectorAll('[data-cf-dropdown]').forEach((g) => {
      if (g === exceptGroup) return;
      const p = g.querySelector('[data-cf-dropdown-panel]');
      const b = g.querySelector('[data-cf-dropdown-toggle]');
      if (p) p.hidden = true;
      if (b) b.setAttribute('aria-expanded', 'false');
    });
  }

  // Toggle on button click
  document.addEventListener('click', (e) => {
    const toggleBtn = e.target.closest('[data-cf-dropdown-toggle]');
    if (toggleBtn) {
      e.stopPropagation();
      const group = toggleBtn.closest('[data-cf-dropdown]');
      const panel = group && group.querySelector('[data-cf-dropdown-panel]');
      if (!panel) return;
      const opening = panel.hidden;
      closeAllDropdowns(group);
      panel.hidden = !opening;
      toggleBtn.setAttribute('aria-expanded', String(opening));
      return;
    }
    // Click outside — close all
    if (!e.target.closest('[data-cf-dropdown]')) {
      closeAllDropdowns(null);
    }
  });

  // No reinit needed — delegation handles it automatically
  window._reinitTopBarDropdowns = function () {};

  /* ============================================================
     COPY LINK — product share block
     ============================================================ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-copy-link]');
    if (!btn) return;
    navigator.clipboard.writeText(btn.dataset.copyLink).then(() => {
      const orig = btn.innerHTML;
      btn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>';
      setTimeout(() => { btn.innerHTML = orig; }, 2000);
      if (window.showToast) window.showToast('Link copied!');
    }).catch(() => {
      if (window.showToast) window.showToast('Could not copy link', true);
    });
  });

  /* ============================================================
     MARKET MODAL — close all other overlays when the market button is clicked
     (The market modal's own open logic is in the localization section JS.)
     ============================================================ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-market-open]');
    if (!btn) return;
    // If the click came from inside the drawer it is already handled (and stopped)
    // by the drawer's own delegated listener. This catches header-level clicks only.
    window.Overlays.closeAll('market');
  });

  /* ============================================================
     CART DRAWER — open trigger (cart drawer JS is in cart-drawer.js)
     ============================================================ */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      window.Overlays.closeAll('cart');   // close drawer, search, etc. first
      document.querySelector('cart-drawer')?.open();
    }
  });

  // Register a cart-drawer close so other overlays can dismiss it
  // cart-drawer is a custom element — try to close it via its API
  window.Overlays.register('cart', function () {
    const cd = document.querySelector('cart-drawer');
    if (cd && typeof cd.close === 'function') cd.close();
  });

  /* ============================================================
     PRODUCT PAGE — variant picker, gallery, sticky ATC, size guide
     ============================================================ */
  (function () {
    const form = document.querySelector('[data-pf]');
    if (!form) return;

    const variantSelect = form.querySelector('[data-variant-select]');
    const addBtn = form.querySelector('[data-add-btn]');
    const addBtnText = form.querySelector('[data-add-btn-text]');
    const priceEl = document.querySelector('[data-product-price]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const stickyBtn = document.querySelector('[data-sticky-add]');

    // Parse variants from JSON blob
    let variants = [];
    try {
      variants = JSON.parse(document.getElementById('product-variants-json')?.textContent || '[]');
    } catch (_) {}

    var formatMoney = window.theme && window.theme.formatMoney
      ? window.theme.formatMoney.bind(window.theme)
      : function (c) { return (parseInt(c, 10) / 100).toFixed(2); };

    function getSelectedOptions() {
      const opts = {};
      form.querySelectorAll('[data-option-input]:checked').forEach((inp) => {
        opts[inp.name] = inp.value;
      });
      return opts;
    }

    function findVariant(selectedOpts) {
      // Build an ordered array of selected values that matches v.options order.
      // selectedOpts keys are "options[Name]" inserted in DOM order, which matches
      // product.options_with_values position order — same order as v.options.
      const selectedVals = Object.values(selectedOpts);
      if (!selectedVals.length) return null;
      return variants.find((v) =>
        v.options.length === selectedVals.length &&
        v.options.every((val, i) => val === selectedVals[i])
      ) || null;
    }

    function updatePrice(variant) {
      if (!variant || !priceEl) return;
      let html = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const saving = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
        html = `<span class="product-info__price-sale">${formatMoney(variant.price)}</span>
                <s class="product-info__price-compare">${formatMoney(variant.compare_at_price)}</s>
                <span class="product-info__price-badge">Save ${saving}%</span>`;
      } else {
        html = `<span class="product-info__price-regular">${formatMoney(variant.price)}</span>`;
      }
      priceEl.innerHTML = html;
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    }

    function updateATC(variant) {
      const available = variant && variant.available;
      const isPreorder = !!document.querySelector('[data-preorder="true"]');
      const text = available ? (isPreorder ? 'Pre-order' : 'Add to bag') : 'Sold out';
      if (addBtn) { addBtn.disabled = !available; }
      if (addBtnText) addBtnText.textContent = text;
      if (stickyBtn) { stickyBtn.disabled = !available; stickyBtn.textContent = text; }
      if (variantSelect && variant) variantSelect.value = variant.id;
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }

    function updateStickyVariant(variant) {
      const el = document.querySelector('[data-sticky-variant]');
      if (el && variant) el.textContent = variant.title === 'Default Title' ? '' : variant.title;
    }

    function updateSku(variant) {
      const el = document.querySelector('[data-variant-sku]');
      if (!el) return;
      const skuWrap = el.closest('.product-info__meta');
      if (variant && variant.sku) {
        el.textContent = variant.sku;
        if (skuWrap) skuWrap.hidden = false;
      } else {
        if (skuWrap) skuWrap.hidden = true;
      }
    }

    function getInvDataForVariant(variantId) {
      // Read inventory data from the server-rendered <option> data attributes —
      // these are always accurate because Liquid renders them at request time.
      // The product.variants | json blob does not reliably include inventory_quantity
      // on the client side, so we use data attributes as the authoritative source.
      if (!variantSelect) return {};
      const opt = variantSelect.querySelector(`option[value="${variantId}"]`);
      if (!opt) return {};
      return {
        available:            opt.dataset.available === 'true',
        inventory_management: opt.dataset.invMgmt || null,
        inventory_quantity:   parseInt(opt.dataset.invQty, 10)
      };
    }

    function updateInventory(variant) {
      const el = document.querySelector('[data-inventory-status]');
      if (!el) return;

      const cfg       = (window.theme && window.theme.inv) || {};
      const threshold = cfg.lowThreshold || 5;
      const showCount = cfg.showCount !== false;
      const lowLabel  = cfg.lowLabel || 'Low stock — Order soon';
      const inLabel   = cfg.inLabel  || 'In stock';
      const warnSvg   = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>';
      const checkSvg  = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

      let html = '';

      if (!variant) {
        if (el.innerHTML !== '') el.innerHTML = '';
        return;
      }

      // Prefer data-attribute inventory data (server-rendered, always accurate)
      // over whatever the JSON blob may have returned.
      const inv       = getInvDataForVariant(variant.id);
      const available = (inv.available !== undefined) ? inv.available : variant.available;
      const invMgmt   = (inv.inventory_management !== undefined) ? inv.inventory_management : variant.inventory_management;
      const invQty    = !isNaN(inv.inventory_quantity) ? inv.inventory_quantity : (variant.inventory_quantity || 0);

      if (!available) {
        html = '<span class="inv-badge inv-badge--out">Sold out</span>';
      } else if (invMgmt !== 'shopify') {
        html = `<span class="inv-badge inv-badge--in">${checkSvg} ${inLabel}</span>`;
      } else {
        const countPrefix = showCount && invQty > 0 ? `${invQty} in stock — ` : '';
        if (invQty <= 0) {
          html = '';
        } else if (invQty <= threshold) {
          html = `<span class="inv-badge inv-badge--low">${warnSvg} ${countPrefix}${lowLabel}</span>`;
        } else {
          html = `<span class="inv-badge inv-badge--in">${checkSvg} ${countPrefix}${inLabel}</span>`;
        }
      }

      if (el.innerHTML !== html) el.innerHTML = html;
    }

    function updateGallery(variant) {
      if (!variant || !variant.featured_image) return;
      // featured_image.position is 1-based; gallery slides are 0-based
      const idx = variant.featured_image.position - 1;
      if (idx >= 0) showSlide(idx);
    }

    function updateUrl(variant) {
      if (!variant) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({ variantId: variant.id }, '', url.toString());
    }

    function onOptionChange() {
      const selectedOpts = getSelectedOptions();
      const variant = findVariant(selectedOpts);

      // Update selected option labels
form.querySelectorAll('[data-option-input]:checked').forEach((input) => {
  const label = form.querySelector(
    `[data-selected-option="${input.dataset.optionPosition}"]`
  );

  if (label) {
    label.textContent = input.value;
  }
});

      updatePrice(variant);
      updateATC(variant);
      updateGallery(variant);
      updateUrl(variant);
      updateStickyVariant(variant);
      updateSku(variant);
      updateInventory(variant);
    }

    // ── Quantity stepper ──
    const qtyInput = form.querySelector('[data-quantity-input]');
    form.addEventListener('click', (e) => {
      if (!qtyInput) return;
      if (e.target.closest('[data-qty-plus]')) {
        qtyInput.value = Math.min(99, parseInt(qtyInput.value || 1) + 1);
      }
      if (e.target.closest('[data-qty-minus]')) {
        qtyInput.value = Math.max(1, parseInt(qtyInput.value || 1) - 1);
      }
    });
    if (qtyInput) {
      qtyInput.addEventListener('change', () => {
        const v = parseInt(qtyInput.value);
        if (isNaN(v) || v < 1) qtyInput.value = 1;
        if (v > 99) qtyInput.value = 99;
      });
    }

    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-option-input]')) onOptionChange();
    });

    // ── Init inventory on page load ──
    // Seed from variantSelect.value (the ID already set by Liquid) so we always
    // match the server-rendered state. If the variant can't be found in the JSON
    // (empty array, parse failure, etc.) we leave the server-rendered badge intact
    // rather than wiping it.
    (function initInventory() {
      const initId = variantSelect ? parseInt(variantSelect.value, 10) : NaN;
      const initVariant = !isNaN(initId)
        ? variants.find((v) => v.id === initId)
        : variants[0];
      if (initVariant) updateInventory(initVariant);
    })();

    // ── Gallery ──
    const galleryEl   = document.querySelector('[data-gallery]');
    const galleryMain = document.querySelector('[data-gallery-main]');
    const thumbs      = document.querySelectorAll('[data-thumb-idx]');
    const slides      = document.querySelectorAll('[data-gallery-slide]');
    const prevBtn     = document.querySelector('[data-gallery-prev]');
    const nextBtn     = document.querySelector('[data-gallery-next]');
    const counterCur  = document.querySelector('[data-gallery-current]');
    const galleryMode = galleryEl ? galleryEl.dataset.galleryMode : 'stacked';
    let currentSlide  = 0;
    const totalSlides = slides.length;

    function setActiveThumb(idx) {
      thumbs.forEach((t) => t.classList.toggle('is-active', parseInt(t.dataset.thumbIdx) === idx));
      if (counterCur) counterCur.textContent = idx + 1;
    }

    function showSlide(idx) {
      if (totalSlides === 0) return;
      currentSlide = ((idx % totalSlides) + totalSlides) % totalSlides; // wrap
      slides.forEach((s) => s.classList.toggle('is-active', parseInt(s.dataset.gallerySlide) === currentSlide));
      setActiveThumb(currentSlide);

      const isMobile = window.innerWidth < 900;

      if (isMobile) {
        // Mobile: horizontal scroll-snap carousel — scroll galleryMain to the right slot
        if (galleryMain) {
          galleryMain.scrollTo({ left: galleryMain.offsetWidth * currentSlide, behavior: 'smooth' });
        }
      } else if (galleryMode === 'stacked') {
        // Desktop stacked: scroll target slide into view vertically
        const targetSlide = document.querySelector('[data-gallery-slide="' + currentSlide + '"]');
        if (targetSlide) targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      // Desktop slider: is-active toggle is sufficient (CSS handles visibility)
    }

    // Desktop stacked: keep active thumb in sync as user scrolls vertically
    if (galleryMode === 'stacked' && window.innerWidth >= 900 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        // Pick the slide with the greatest intersection ratio
        let best = null;
        entries.forEach((entry) => {
          if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
        });
        if (best && best.intersectionRatio > 0) {
          const idx = parseInt(best.target.dataset.gallerySlide);
          if (!isNaN(idx)) { currentSlide = idx; setActiveThumb(idx); }
        }
      }, { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: '-10% 0px -10% 0px' });
      slides.forEach((s) => observer.observe(s));
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => showSlide(parseInt(thumb.dataset.thumbIdx)));
    });

    // Prev / Next arrows
    if (prevBtn) prevBtn.addEventListener('click', () => showSlide(currentSlide - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => showSlide(currentSlide + 1));

    // Keyboard arrow keys when gallery is focused
    if (galleryEl) {
      galleryEl.setAttribute('tabindex', '-1');
      document.addEventListener('keydown', (e) => {
        if (!document.querySelector('.product-lightbox[aria-hidden="false"]') === false) return; // lightbox open — handled separately
        if (e.key === 'ArrowLeft')  showSlide(currentSlide - 1);
        if (e.key === 'ArrowRight') showSlide(currentSlide + 1);
      });
    }

    // Touch swipe on gallery main
    if (galleryMain && totalSlides > 1) {
      let touchStartX = 0;
      galleryMain.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
      galleryMain.addEventListener('touchend', (e) => {
        const diff = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) showSlide(diff > 0 ? currentSlide + 1 : currentSlide - 1);
      }, { passive: true });

      // Keep counter + thumbs in sync when CSS scroll-snap settles on a slide
      // (momentum scrolling can bypass the touchend handler above).
      let scrollTimer;
      galleryMain.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const idx = Math.round(galleryMain.scrollLeft / galleryMain.offsetWidth);
          if (idx !== currentSlide) { currentSlide = idx; setActiveThumb(idx); }
        }, 80);
      }, { passive: true });
    }

    // ── Lightbox — full-screen on image click ──
    (function () {
      if (!galleryMain) return;

      // Build lightbox DOM once
      const lb = document.createElement('div');
      lb.className = 'product-lightbox';
      lb.setAttribute('aria-hidden', 'true');
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-label', 'Image viewer');
      lb.innerHTML = `
        <div class="product-lightbox__backdrop"></div>
        <div class="product-lightbox__stage">
          <img class="product-lightbox__img" src="" alt="">
          <button class="product-lightbox__close" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
          <button class="product-lightbox__prev" aria-label="Previous">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <button class="product-lightbox__next" aria-label="Next">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
          <span class="product-lightbox__counter"><span data-lb-cur>1</span> / <span data-lb-total>1</span></span>
        </div>`;
      document.body.appendChild(lb);

      const lbImg   = lb.querySelector('.product-lightbox__img');
      const lbCur   = lb.querySelector('[data-lb-cur]');
      const lbTotal = lb.querySelector('[data-lb-total]');
      let lbIdx = 0;

      // Collect full-res image URLs from slides
      function getSlideImages() {
        return Array.from(slides).map((s) => {
          const img = s.querySelector('img');
          if (!img) return null;
          // Use the largest srcset entry or fall back to src
          const srcset = img.srcset;
          if (srcset) {
            const entries = srcset.split(',').map((e) => e.trim().split(' '));
            entries.sort((a, b) => parseInt(b[1]) - parseInt(a[1]));
            if (entries[0]) return entries[0][0];
          }
          return img.src;
        }).filter(Boolean);
      }

      function openLightbox(idx) {
        const images = getSlideImages();
        if (!images.length) return;
        lbIdx = ((idx % images.length) + images.length) % images.length;
        lbImg.src = images[lbIdx];
        lbImg.alt = slides[lbIdx]?.querySelector('img')?.alt || '';
        lbTotal.textContent = images.length;
        lbCur.textContent = lbIdx + 1;
        lb.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        lb.querySelector('.product-lightbox__close').focus();
      }

      function closeLightbox() {
        lb.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }

      function lbNav(dir) {
        const images = getSlideImages();
        lbIdx = ((lbIdx + dir + images.length) % images.length);
        lbImg.src = images[lbIdx];
        lbImg.alt = slides[lbIdx]?.querySelector('img')?.alt || '';
        lbCur.textContent = lbIdx + 1;
        // Sync main gallery too
        showSlide(lbIdx);
      }

      // Open on image click (not on video slides)
      galleryMain.addEventListener('click', (e) => {
        const slide = e.target.closest('[data-gallery-slide]');
        if (!slide || slide.classList.contains('product-gallery__slide--video')) return;
        if (e.target.closest('button')) return; // don't intercept arrows
        openLightbox(parseInt(slide.dataset.gallerySlide) || currentSlide);
      });

      lb.querySelector('.product-lightbox__close').addEventListener('click', closeLightbox);
      lb.querySelector('.product-lightbox__backdrop').addEventListener('click', closeLightbox);
      lb.querySelector('.product-lightbox__prev').addEventListener('click', () => lbNav(-1));
      lb.querySelector('.product-lightbox__next').addEventListener('click', () => lbNav(1));

      document.addEventListener('keydown', (e) => {
        if (lb.getAttribute('aria-hidden') === 'true') return;
        if (e.key === 'Escape')     closeLightbox();
        if (e.key === 'ArrowLeft')  lbNav(-1);
        if (e.key === 'ArrowRight') lbNav(1);
      });

      // Touch swipe inside lightbox
      let lbTouchX = 0;
      lb.addEventListener('touchstart', (e) => { lbTouchX = e.touches[0].clientX; }, { passive: true });
      lb.addEventListener('touchend',   (e) => {
        const diff = lbTouchX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) lbNav(diff > 0 ? 1 : -1);
      }, { passive: true });
    })();

    // ── Sticky ATC ──
    const stickyATC = document.querySelector('[data-sticky-atc]');
    if (stickyATC && addBtn) {
      let atcOutOfView  = false;
      let footerInView  = false;

      function syncStickyATC() {
        stickyATC.classList.toggle('is-visible', atcOutOfView && !footerInView);
      }

      // Show when the ATC button scrolls out of view
      new IntersectionObserver(([entry]) => {
        atcOutOfView = !entry.isIntersecting;
        syncStickyATC();
      }, { threshold: 0 }).observe(addBtn);

      // Hide when the footer scrolls into view
      const footer = document.querySelector('.site-footer');
      if (footer) {
        new IntersectionObserver(([entry]) => {
          footerInView = entry.isIntersecting;
          syncStickyATC();
        }, { threshold: 0 }).observe(footer);
      }

      stickyBtn?.addEventListener('click', () => addBtn.click());
    }

    // ── Size guide modal ──
    const sizeGuideModal = document.querySelector('[data-size-guide-modal]');
    if (sizeGuideModal) {
      // Portal to <body> so position:fixed + z-index beats the sticky header stacking context
      document.body.appendChild(sizeGuideModal);
      let _sizeGuideOpen = false;
      let _sgTrigger = null;
      let _sgTrap = null;

      function closeSizeGuide() {
        if (!_sizeGuideOpen) return;
        _sizeGuideOpen = false;
        if (_sgTrap) { _sgTrap.deactivate(); _sgTrap = null; }
        sizeGuideModal.hidden = true;
        document.body.style.overflow = '';
      }

      window.Overlays.register('size-guide', closeSizeGuide);

      document.querySelectorAll('[data-size-guide-open]').forEach((btn) => {
        btn.addEventListener('click', () => {
          window.Overlays.closeAll('size-guide');
          _sizeGuideOpen = true;
          _sgTrigger = document.activeElement;
          sizeGuideModal.hidden = false;
          document.body.style.overflow = 'hidden';
          requestAnimationFrame(() => {
            _sgTrap = window.createFocusTrap(sizeGuideModal, _sgTrigger);
            _sgTrap.activate();
          });
        });
      });
      sizeGuideModal.querySelectorAll('[data-size-guide-close]').forEach((el) => {
        el.addEventListener('click', closeSizeGuide);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !sizeGuideModal.hidden) closeSizeGuide();
      });
    }

    // ── Product tabs (below fold) ──
    document.querySelectorAll('.product-tabs__tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        const idx = parseInt(tab.dataset.tab);
        document.querySelectorAll('.product-tabs__tab').forEach((t) => {
          t.classList.toggle('is-active', parseInt(t.dataset.tab) === idx);
          t.setAttribute('aria-selected', parseInt(t.dataset.tab) === idx);
        });
        document.querySelectorAll('.product-tabs__panel').forEach((p) => {
          p.classList.toggle('is-active', parseInt(p.dataset.panel) === idx);
        });
      });
    });
  })();
  /* ============================================================
     SLIDER AUTOPLAY — shared across product / UGC / logo sliders
     ============================================================
     Each slider container needs:
       data-autoplay="true"
       data-interval="5000"   (ms)
     The scrollable track is found by a per-type selector.
     ============================================================ */
  function setupSliderAutoplay(container, track) {
    if (!container || !track) return;
    if (container.dataset.autoplayInit) return; // prevent double-init
    container.dataset.autoplayInit = '1';

    const interval = parseInt(container.dataset.interval, 10) || 5000;
    let timer = null;
    let paused = false;

    function tick() {
      if (paused) return;
      const first = track.firstElementChild;
      if (!first || first.offsetWidth === 0) return; // not yet laid out
      const gap    = parseFloat(getComputedStyle(track).gap) || 16;
      const amount = first.offsetWidth + gap;
      const atEnd  = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      if (atEnd) {
        // Jump back to start (instant, then let next interval do the first forward scroll)
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: amount, behavior: 'smooth' });
      }
    }

    function start() {
      paused = false;
      clearInterval(timer);
      timer = setInterval(tick, interval);
    }
    function stop() {
      paused = true;
      clearInterval(timer);
    }

    start();
    container.addEventListener('mouseenter', stop);
    container.addEventListener('mouseleave', start);
    container.addEventListener('touchstart', stop, { passive: true });
    // Resume after user finishes swiping — wait one full interval so they can read
    container.addEventListener('touchend', () => setTimeout(start, interval), { passive: true });
  }

  // Called on DOMContentLoaded and again after dynamic content loads (recently-viewed)
  window.initSliderAutoplay = function () {
    // product-slider sections (featured-products, recently-viewed, recommendations, style-with)
    document.querySelectorAll('.product-slider[data-autoplay="true"]').forEach((el) => {
      setupSliderAutoplay(el, el.querySelector('.product-slider__track'));
    });
    // UGC slider
    document.querySelectorAll('.ugc-slider[data-autoplay="true"]').forEach((el) => {
      setupSliderAutoplay(el, el.querySelector('[data-ugc-track]'));
    });
    // Logo list
    document.querySelectorAll('.logo-list[data-autoplay="true"]').forEach((el) => {
      setupSliderAutoplay(el, el.querySelector('[data-logo-track]'));
    });
  };

  // Run after DOM is ready (defer guarantees this fires before DOMContentLoaded)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.initSliderAutoplay);
  } else {
    // DOM already parsed (e.g. script injected dynamically)
    window.initSliderAutoplay();
  }

  /* ============================================================
     QUICK-ADD — product card size buttons (AJAX add to cart)
     ============================================================ */
  document.addEventListener('click', (e) => {
    const sizeBtn = e.target.closest('.product-card__size[data-variant-id], .product-card__add-btn[data-variant-id]');
    if (!sizeBtn) return;
    e.preventDefault();

    const variantId = sizeBtn.dataset.variantId;
    sizeBtn.textContent = '…';
    sizeBtn.disabled = true;

    (async () => {
      try {
        const res = await fetch(window.theme.routes.cartAdd + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        if (!res.ok) throw new Error('Add failed');

        sizeBtn.textContent = '✓';
        if (window.showToast) window.showToast('Added to bag');

        // Refresh drawer HTML with new cart contents, then open
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.refresh) {
          await drawer.refresh();
        }
        document.querySelector('cart-drawer')?.open();

        // Update header cart count
        const cartRes = await fetch(window.theme.routes.cart + '.js');
        const cart = await cartRes.json();
        const countEl = document.querySelector('[data-cart-count]');
        if (countEl) countEl.textContent = cart.item_count;

        document.dispatchEvent(new CustomEvent('cart:item-added'));

      } catch {
        sizeBtn.textContent = '!';
        if (window.showToast) window.showToast('Could not add to bag', true);
      } finally {
        setTimeout(() => {
          sizeBtn.disabled = false;
          const label = sizeBtn.getAttribute('aria-label');
          if (label) sizeBtn.textContent = label.split(' —')[0];
        }, 1500);
      }
    })();
  });

  /* ============================================================
     WISHLIST TOGGLE — localStorage-based, visual only
     ============================================================ */
  const WISHLIST_KEY = 'af_wishlist';

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; }
    catch { return []; }
  }

  function saveWishlist(list) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  }

  function updateWishlistBadge() {
    const count = getWishlist().length;
    const badge = document.querySelector('[data-wishlist-count]');
    if (!badge) return;
    badge.textContent = count;
    badge.hidden = count === 0;
  }

  // Initialize active states on load
  const wishlist = getWishlist();
  document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
    if (wishlist.includes(btn.dataset.productHandle)) {
      btn.classList.add('is-active');
    }
  });
  updateWishlistBadge();

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = btn.dataset.productHandle;
    const list = getWishlist();
    const idx = list.indexOf(handle);
    if (idx > -1) {
      list.splice(idx, 1);
      btn.classList.remove('is-active');
    } else {
      list.push(handle);
      btn.classList.add('is-active');
    }
    saveWishlist(list);
    updateWishlistBadge();
  });
})();

/* ============================================================
   COUNTDOWN TIMERS — product card [data-countdown]
   Reads ISO date string from data-countdown attribute.
   Displays: "DDd HH:MM:SS" — matching the reference design.
   Hides when expired (sets data-expired attribute).
   ============================================================ */
(function () {
  'use strict';

  function pad(n) { return String(n).padStart(2, '0'); }

  function renderCountdown(el, endDate) {
    var diff = endDate - Date.now();
    if (diff <= 0) {
      el.dataset.expired = '';
      el.textContent = '';
      // Propagate expiry to flash-strip parent if present
      var strip = el.closest('[data-flash-strip]');
      if (strip) strip.dataset.expired = '';
      return false; // stop ticking
    }
    var totalSec = Math.floor(diff / 1000);
    var days     = Math.floor(totalSec / 86400);
    var hours    = Math.floor((totalSec % 86400) / 3600);
    var mins     = Math.floor((totalSec % 3600) / 60);
    var secs     = totalSec % 60;
    el.textContent = (days > 0 ? days + 'D  ' : '') + pad(hours) + ':' + pad(mins) + ':' + pad(secs);
    return true;
  }

  function initCountdowns(root) {
    root = root || document;
    var els = root.querySelectorAll('[data-countdown]');
    els.forEach(function (el) {
      if (el._countdownRunning) return;
      var endDate = new Date(el.dataset.countdown);
      if (isNaN(endDate)) return;
      el._countdownRunning = true;

      if (!renderCountdown(el, endDate)) return;
      var timer = setInterval(function () {
        if (!renderCountdown(el, endDate)) {
          clearInterval(timer);
          el._countdownRunning = false;
        }
      }, 1000);
    });
  }

  // Init on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initCountdowns(); });
  } else {
    initCountdowns();
  }

  // Re-init after AJAX (collection filter, wishlist, etc.)
  window._reinitCountdowns = initCountdowns;
})();

/* ============================================================
   PDP FLASH SALE TIMER — [data-flash-timer]
   Renders D / H / M / S into child [data-flash-*] spans.
   Hides parent .pdp-flash-sale when expired.
   ============================================================ */
(function () {
  'use strict';
  function pad(n) { return String(n).padStart(2, '0'); }

  function tickFlash(timer, endDate, banner) {
    var diff = endDate - Date.now();
    if (diff <= 0) {
      banner.dataset.expired = '';
      return false;
    }
    var totalSec = Math.floor(diff / 1000);
    var days  = Math.floor(totalSec / 86400);
    var hours = Math.floor((totalSec % 86400) / 3600);
    var mins  = Math.floor((totalSec % 3600) / 60);
    var secs  = totalSec % 60;
    var d = timer.querySelector('[data-flash-days]');
    var h = timer.querySelector('[data-flash-hrs]');
    var m = timer.querySelector('[data-flash-mins]');
    var s = timer.querySelector('[data-flash-secs]');
    if (d) d.textContent = pad(days);
    if (h) h.textContent = pad(hours);
    if (m) m.textContent = pad(mins);
    if (s) s.textContent = pad(secs);
    return true;
  }

  function initFlashTimers() {
    document.querySelectorAll('[data-flash-timer]').forEach(function (timer) {
      if (timer._flashInit) return;
      timer._flashInit = true;
      var endDate = new Date(timer.dataset.end);
      if (isNaN(endDate)) return;
      var banner = timer.closest('.pdp-flash-sale');
      if (!tickFlash(timer, endDate, banner)) return;
      var iv = setInterval(function () {
        if (!tickFlash(timer, endDate, banner)) clearInterval(iv);
      }, 1000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFlashTimers);
  } else {
    initFlashTimers();
  }
})();

/* ============================================================
   PRODUCT CARD — IMAGE SLIDER (hover mode)
   Cycles through slides on hover using a timed interval.
   Works after AJAX swaps via event delegation on document.
   ============================================================ */
(function () {
  function goToSlide(card, dir) {
    var slides = card.querySelectorAll('.product-card__slide');
    var dots   = card.querySelectorAll('.product-card__dot');
    if (slides.length < 2) return;
    var current = card.querySelector('.product-card__slide.is-active');
    var idx = Array.from(slides).indexOf(current);
    slides[idx].classList.remove('is-active');
    dots[idx] && dots[idx].classList.remove('is-active');
    idx = ((idx + dir) % slides.length + slides.length) % slides.length;
    slides[idx].classList.add('is-active');
    dots[idx] && dots[idx].classList.add('is-active');
  }

  // Arrow button clicks
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-card-prev], [data-card-next]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var card = btn.closest('.product-card--hover-slider');
    if (!card) return;
    var dir = btn.hasAttribute('data-card-next') ? 1 : -1;
    goToSlide(card, dir);
  });

  // Touch: arrow taps only
  document.addEventListener('touchstart', function (e) {
    var btn = e.target.closest('[data-card-prev], [data-card-next]');
    if (!btn) return;
    var card = btn.closest('.product-card--hover-slider');
    if (!card) return;
    var dir = btn.hasAttribute('data-card-next') ? 1 : -1;
    goToSlide(card, dir);
  }, { passive: true });
})();

/* ============================================================
   QUICK VIEW — uses /products/{handle}.js, builds UI in JS
   ============================================================ */
(function () {
  'use strict';

  var modal    = document.getElementById('quick-view-modal');
  if (!modal) return;

  var skeleton = modal.querySelector('[data-qv-skeleton]');
  var content  = modal.querySelector('[data-qv-content]');
  var cache    = {};
  var activeHandle = null;

  /* ── Open / Close ── */
  let _qvOpen = false;
  let _qvTrigger = null;
  let _qvTrap = null;

  function open() {
    window.Overlays.closeAll('quick-view');   // close drawer, search, cart, etc. first
    _qvOpen = true;
    _qvTrigger = document.activeElement;
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      _qvTrap = window.createFocusTrap(modal, _qvTrigger);
      _qvTrap.activate();
    });
  }
  function close() {
    if (!_qvOpen) return;
    _qvOpen = false;
    if (_qvTrap) { _qvTrap.deactivate(); _qvTrap = null; }
    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    activeHandle = null;
  }

  // Register so other overlays can close quick-view
  window.Overlays.register('quick-view', close);

  modal.querySelectorAll('[data-qv-close]').forEach(function (el) {
    el.addEventListener('click', close);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !modal.hasAttribute('hidden')) close();
  });

  /* ── Trigger ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-view][data-product-handle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var handle = btn.dataset.productHandle;
    if (!handle) return;

    // Already open with same product
    if (handle === activeHandle) { open(); return; }
    activeHandle = handle;

    // Show modal with skeleton
    skeleton.removeAttribute('hidden');
    content.setAttribute('hidden', '');
    content.innerHTML = '';
    open();

    // From cache
    if (cache[handle]) { render(cache[handle]); return; }

    // Fetch product JSON
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        cache[handle] = product;
        if (activeHandle === handle) render(product);
      })
      .catch(function () {
        close();
      });
  });

  /* ── Build and show content ── */
  function render(p) {
    skeleton.setAttribute('hidden', '');
    content.innerHTML = buildHTML(p);
    content.removeAttribute('hidden');
    initPicker(p);
    if (typeof window._reinitCountdowns === 'function') window._reinitCountdowns(content);
    // Wishlist sync
    try {
      var wl = JSON.parse(localStorage.getItem('af_wishlist') || '[]');
      content.querySelectorAll('[data-wishlist-toggle]').forEach(function (b) {
        b.classList.toggle('is-active', wl.includes(b.dataset.productHandle));
      });
    } catch (_) {}
  }

  /* ── Flash tag parser — handles both array (Recommendations API) and string (product .js) ── */
  function parseFlashTags(tags) {
    var arr = Array.isArray(tags)
      ? tags
      : String(tags || '').split(',').map(function (t) { return t.trim(); });
    if (arr.indexOf('flash-sale') === -1) return null;
    var end = null, label = 'Flash Sale';
    arr.forEach(function (t) {
      if (t.indexOf('flash-end:') === 0) end = t.slice(10);
      if (t.indexOf('flash-label:') === 0) label = t.slice(12).replace(/-/g, ' ');
    });
    return { end: end, label: label };
  }

  /* ── Build HTML from product JSON ── */
  function buildHTML(p) {
    var v = p.variants[0];
    var imgUrl = p.featured_image
      ? imgSrc(typeof p.featured_image === 'object' ? p.featured_image.src : p.featured_image, 800)
      : '';

    // Thumbnail strip
    var thumbsHTML = '';
    if (p.images && p.images.length > 1) {
      thumbsHTML = '<div class="qv-product__thumbs">';
      p.images.slice(0, 5).forEach(function (img, i) {
        var src = imgSrc(img, 160);
        thumbsHTML += '<button type="button" class="qv-product__thumb' + (i === 0 ? ' is-active' : '')
          + '" data-qv-thumb="' + imgSrc(img, 900) + '">'
          + '<img src="' + src + '" alt="" loading="lazy">'
          + '</button>';
      });
      thumbsHTML += '</div>';
    }

    // Price
    var priceHTML = buildPrice(v);

    // Options
    var optionsHTML = '';
    if (p.options && !(p.options.length === 1 && p.options[0] === 'Title')) {
      p.options.forEach(function (optName, optIdx) {
        var isColor = /colo(u)?r/i.test(optName);
        var values  = getOptionValues(p, optIdx);
        var inputsHTML = '';

        if (isColor) {
          inputsHTML = '<div class="qv-product__swatches">';
          values.forEach(function (val, i) {
            var bg = val.toLowerCase().replace(/\s+/g, '');
            inputsHTML += '<label class="qv-swatch" title="' + esc(val) + '">'
              + '<input type="radio" name="option' + optIdx + '" value="' + esc(val) + '" data-qv-option data-opt-idx="' + optIdx + '"' + (i === 0 ? ' checked' : '') + '>'
              + '<span class="qv-swatch__dot" style="background:' + bg + ';"></span>'
              + '<span class="qv-swatch__label">' + esc(val) + '</span>'
              + '</label>';
          });
          inputsHTML += '</div>';
        } else {
          inputsHTML = '<div class="qv-product__pills">';
          values.forEach(function (val, i) {
            var available = isOptionAvailable(p, optIdx, val);
            inputsHTML += '<label class="qv-pill' + (available ? '' : ' qv-pill--oos') + '">'
              + '<input type="radio" name="option' + optIdx + '" value="' + esc(val) + '" data-qv-option data-opt-idx="' + optIdx + '"'
              + (i === 0 ? ' checked' : '')
              + (available ? '' : ' disabled')
              + '>'
              + esc(val)
              + '</label>';
          });
          inputsHTML += '</div>';
        }

        optionsHTML += '<div class="qv-product__option">'
          + '<p class="qv-product__option-name">' + esc(optName) + '</p>'
          + inputsHTML
          + '</div>';
      });
    }

    var atcText = v.available ? 'Add to bag' : 'Sold out';
    var descText = p.body_html
      ? stripHTML(p.body_html).substring(0, 180).trim() + (p.body_html.length > 180 ? '…' : '')
      : '';

    var flash = parseFlashTags(p.tags || []);
    var flashHTML = '';
    if (flash) {
      var boltSvg = '<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13 2L4.09 12.97H11L10 22l9-11.03H13L13 2z"/></svg>';
      flashHTML = '<div class="qv-flash-strip' + (flash.end ? ' has-countdown' : '') + '"'
        + (flash.end ? ' data-flash-strip="' + esc(flash.end) + '"' : '') + '>'
        + '<div class="qv-flash-strip__label">' + boltSvg + esc(flash.label) + '</div>'
        + (flash.end
          ? '<div class="qv-flash-strip__timer" data-countdown="' + esc(flash.end) + '"></div>'
          : '<span class="qv-flash-strip__ends">Limited time offer</span>')
        + '</div>';
    }

    return '<div class="qv-product">'
      + '<div class="qv-product__gallery">'
      +   (imgUrl ? '<img class="qv-product__img" src="' + imgUrl + '" alt="' + esc(p.title) + '">' : '<div class="qv-product__img-placeholder"></div>')
      +   thumbsHTML
      + '</div>'
      + '<div class="qv-product__info">'
      +   '<h2 class="qv-product__title">' + esc(p.title) + '</h2>'
      +   '<div class="qv-product__price" data-qv-price>' + priceHTML + '</div>'
      +   flashHTML
      +   '<form class="qv-product__form" data-qv-form>'
      +     '<input type="hidden" name="id" value="' + v.id + '">'
      +     optionsHTML
      +     '<button type="submit" class="btn btn--primary qv-product__atc" data-qv-atc' + (v.available ? '' : ' disabled') + '>'
      +       atcText
      +     '</button>'
      +   '</form>'
      +   (descText ? '<p class="qv-product__desc">' + esc(descText) + '</p>' : '')
      +   '<a href="/products/' + p.handle + '" class="qv-product__full-link">'
      +     'View full details'
      +     '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>'
      +   '</a>'
      + '</div>'
      + '</div>';
  }

  /* ── Variant picker ── */
  function initPicker(p) {
    var form    = content.querySelector('[data-qv-form]');
    var priceEl = content.querySelector('[data-qv-price]');
    var atcBtn  = content.querySelector('[data-qv-atc]');
    var idInput = form && form.querySelector('[name="id"]');
    if (!form) return;

    function getSelectedOptions() {
      var opts = [];
      form.querySelectorAll('[data-qv-option]:checked').forEach(function (inp) {
        opts[parseInt(inp.dataset.optIdx)] = inp.value;
      });
      return opts;
    }

    function findVariant() {
      var selected = getSelectedOptions();
      return p.variants.find(function (v) {
        return v.options.every(function (val, i) { return val === selected[i]; });
      });
    }

    function update() {
      var v = findVariant();
      if (!v) return;
      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.innerHTML = buildPrice(v);
      if (atcBtn) {
        atcBtn.disabled = !v.available;
        atcBtn.textContent = v.available ? 'Add to bag' : 'Sold out';
      }
    }

    form.addEventListener('change', function (e) {
      if (e.target.matches('[data-qv-option]')) update();
    });

    // Thumbnail switching
    content.querySelectorAll('[data-qv-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var img = content.querySelector('.qv-product__img');
        if (img) img.src = btn.dataset.qvThumb;
        content.querySelectorAll('[data-qv-thumb]').forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
      });
    });

    // ATC
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      var variantId = idInput ? idInput.value : null;
      if (!variantId || !atcBtn || atcBtn.disabled) return;
      var orig = atcBtn.textContent;
      atcBtn.textContent = '…';
      atcBtn.disabled = true;

      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        });
        if (!addRes.ok) throw new Error('add failed');
        await addRes.json();

        // 1. Refresh cart drawer content while QV is still visible
        //    (avoids a gap where no overlay is active during the async fetch)
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.refresh) {
          await drawer.refresh();
        }

        // 2. Now close QV and immediately open cart drawer in one synchronous step
        close();
        document.querySelector('cart-drawer')?.open();

        // 3. Sync header cart count badge
        try {
          const cartData = await fetch(window.theme.routes.cart + '.js').then(r => r.json());
          const countEl = document.querySelector('[data-cart-count]');
          if (countEl) countEl.textContent = cartData.item_count;
        } catch (_) {}

        document.dispatchEvent(new CustomEvent('cart:item-added'));

      } catch (_) {
        atcBtn.textContent = 'Try again';
        atcBtn.disabled = false;
        setTimeout(function () { atcBtn.textContent = orig; }, 2000);
      }
    });
  }

  /* ── Helpers ── */
  function buildPrice(v) {
    if (!v) return '';
    if (v.compare_at_price && v.compare_at_price > v.price) {
      return '<span class="price__sale">' + money(v.price) + '</span>'
           + '<s class="price__compare">' + money(v.compare_at_price) + '</s>';
    }
    return '<span>' + money(v.price) + '</span>';
  }

  var money = window.theme && window.theme.formatMoney
    ? window.theme.formatMoney.bind(window.theme)
    : function (c) { return (parseInt(c, 10) / 100).toFixed(2); };

  function imgSrc(url, w) {
    if (!url) return '';
    return String(url)
      .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|\d+x\d*|\d*x\d+)(\.[a-zA-Z]+)/, '$2')
      .replace(/(\.[a-zA-Z]+)(\?|$)/, function (m, ext, q) { return '_' + w + 'x' + ext + (q || ''); });
  }

  function esc(str) {
    return String(str || '').replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function stripHTML(html) {
    var d = document.createElement('div');
    d.innerHTML = html;
    return d.textContent || d.innerText || '';
  }

  function getOptionValues(p, optIdx) {
    var vals = [];
    p.variants.forEach(function (v) {
      var val = v.options[optIdx];
      if (val && vals.indexOf(val) === -1) vals.push(val);
    });
    return vals;
  }

  function isOptionAvailable(p, optIdx, val) {
    return p.variants.some(function (v) { return v.options[optIdx] === val && v.available; });
  }

})();

/* ============================================================
   COLLECTION GRID — COLUMN SWITCHER
   Desktop: data-cols="2|4|6"  → collection__grid--cols-{n}
   Mobile:  data-mob-cols="1|2" → collection__grid--mob-cols-{n}
   Each persists to its own localStorage key.
   ============================================================ */
(function () {
  var LS_DESKTOP = 'af_grid_cols';
  var LS_MOBILE  = 'af_grid_mob_cols';

  function setDesktopCols(cols, save) {
    var grid = document.getElementById('collection-grid');
    if (!grid) return;
    // Explicit remove is more reliable than regex replace across mobile browsers
    grid.classList.remove(
      'collection__grid--cols-2',
      'collection__grid--cols-3',
      'collection__grid--cols-4',
      'collection__grid--cols-6'
    );
    grid.classList.add('collection__grid--cols-' + cols);
    document.querySelectorAll('[data-cols]').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.dataset.cols === String(cols));
    });
    if (save) { try { localStorage.setItem(LS_DESKTOP, String(cols)); } catch (_) {} }
  }

  function setMobileCols(cols, save) {
    var grid = document.getElementById('collection-grid');
    if (!grid) return;
    // Explicit remove is more reliable than regex replace across mobile browsers
    grid.classList.remove(
      'collection__grid--mob-cols-1',
      'collection__grid--mob-cols-2'
    );
    grid.classList.add('collection__grid--mob-cols-' + cols);
    document.querySelectorAll('[data-mob-cols]').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.dataset.mobCols === String(cols));
    });
    if (save) { try { localStorage.setItem(LS_MOBILE, String(cols)); } catch (_) {} }
  }

  // Bind onclick directly to every col button so iOS Chrome's SVG-touch
  // quirks (closest() sometimes failing on SVG children) can't break it.
  function bindColButtons() {
    document.querySelectorAll('[data-cols]').forEach(function (btn) {
      btn.onclick = function () { setDesktopCols(btn.dataset.cols, true); };
    });
    document.querySelectorAll('[data-mob-cols]').forEach(function (btn) {
      btn.onclick = function () { setMobileCols(btn.dataset.mobCols, true); };
    });
  }

  function applyStoredCols() {
    var grid = document.getElementById('collection-grid');
    if (!grid) return;

    // Desktop: restore from storage or use theme default
    var savedDesktop = null;
    try { savedDesktop = localStorage.getItem(LS_DESKTOP); } catch (_) {}
    setDesktopCols(savedDesktop || grid.dataset.defaultCols || '4', false);

    // Mobile: restore from storage or use theme default
    var savedMobile = null;
    try { savedMobile = localStorage.getItem(LS_MOBILE); } catch (_) {}
    setMobileCols(savedMobile || grid.dataset.defaultMobCols || '2', false);

    // Re-bind after every DOM swap (toolbar is inside #collection-results)
    bindColButtons();
  }

  function initColSwitcher() {
    if (!document.getElementById('collection-grid')) return;
    applyStoredCols();
  }

  // Expose reinit for collection-filter-ajax.js to call after each DOM swap
  window._reinitColSwitcher = applyStoredCols;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColSwitcher);
  } else {
    initColSwitcher();
  }
})();


/* ============================================================
   STICKY TOOLBAR — adds .is-stuck shadow when toolbar is pinned
   Uses IntersectionObserver on a 1px sentinel above the wrapper.
   ============================================================ */
(function () {
  var wrap = document.querySelector('[data-toolbar-sticky]');
  if (!wrap) return;
  // Create a 1px sentinel div just above the sticky wrap
  var sentinel = document.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none';
  wrap.parentElement.insertBefore(sentinel, wrap);
  new IntersectionObserver(function (entries) {
    wrap.classList.toggle('is-stuck', !entries[0].isIntersecting);
  }, { threshold: 1 }).observe(sentinel);
})();

/* ============================================================
   BACK TO TOP BUTTON
   ============================================================ */
(function () {
  // Inject button into DOM
  var btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'back-to-top';
  btn.setAttribute('aria-label', 'Back to top');
  btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M18 15l-6-6-6 6"/></svg>';
  document.body.appendChild(btn);

  window.addEventListener('scroll', function () {
    btn.classList.toggle('is-visible', window.scrollY > 600);
  }, { passive: true });

  btn.addEventListener('click', function () {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
})();

/* ============================================================
   TOAST NOTIFICATION
   window.showToast(message, isError) — used by quick-add etc.
   ============================================================ */
(function () {
  var toast = document.createElement('div');
  toast.className = 'af-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  document.body.appendChild(toast);

  var timer;
  window.showToast = function (msg, isError) {
    clearTimeout(timer);
    toast.textContent = msg;
    toast.classList.toggle('af-toast--error', !!isError);
    toast.classList.add('is-visible');
    timer = setTimeout(function () { toast.classList.remove('is-visible'); }, 3000);
  };
})();

/* ============================================================
   RECENTLY VIEWED — record product handle on product pages
   ============================================================ */
(function () {
  var KEY = 'af_recently_viewed';
  var MAX = 12;
  // Only run on product pages
  var handle = document.body.dataset.productHandle;
  if (!handle) {
    // Try meta tag fallback
    var meta = document.querySelector('meta[name="product-handle"]');
    if (meta) handle = meta.content;
  }
  if (!handle) return;
  try {
    var list = JSON.parse(localStorage.getItem(KEY)) || [];
    list = list.filter(function (h) { return h !== handle; });
    list.unshift(handle);
    if (list.length > MAX) list = list.slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch (_) {}
})();


